import { type AnyAction, combineReducers } from 'redux';
import type { OpaqueVideoData } from '@lumen5/luminary';
import { Migrator, VideoData } from '@lumen5/luminary';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';

import type { SceneDesign, Template } from 'js/src/client';
import PlainJsStoreORM from 'js/src/data/plain-js-store-orm';
import loadingInfoSlice from 'js/src/data/templates/slices/loadingInfoSlice';
import sceneDesignSelectionSlice from 'js/src/data/templates/slices/sceneDesignSelectionSlice';
import sceneSequenceDesignsSlice from 'js/src/data/templates/slices/sceneSequenceDesignsSlice';
import {
    _setTemplateData,
    clearSceneDesignsForTemplates,
    setActiveTemplateCategoryId,
    setDisplayGroupName,
    setNonRecommendableAssetNames,
    setSceneDesignAttributesRecommendation,
    setSceneDesignData,
    setSceneDesignDisplayGroup,
    setSceneDesignDisplayName,
    setSceneDesignOrders,
    setSceneDesignStatus,
    setTemplateEnabledGlobalTemplates,
    setTemplateIsGlobalTemplate,
    setTemplateAspectRatioMp4Url,
    setTemplateCategoryId,
    setTemplateChartColorOverrides,
    setTemplateColorPalettes,
    setTemplateColorShuffleEnabled,
    setTemplateDefaultAspectRatio,
    setTemplateDefaultCaptionSceneDesign,
    setTemplateDefaultCutawaySceneDesign,
    setTemplateDefaultIconSceneDesign,
    setTemplateDefaultOutroSceneDesign,
    setTemplateDefaultSceneDesign,
    setTemplateDefaultStoryboardSceneDesign,
    setTemplateDefaultTalkingHeadSceneDesign,
    setTemplateDefaultTextSizeOptions,
    setTemplateDefaultVoiceoverSceneDesign,
    setTemplateDisplayName,
    setTemplateImageUrl,
    setTemplateMp4Url,
    setTemplateOrders,
    setTemplateSearchQuery,
    setTemplateStatus,
    setTemplateTextMaxSize,
    setTemplateTextMinSize,
    storeAllSceneDesignIdsPerTemplate,
    storeDisplayGroups,
    storeRecentSelectedSceneDesign,
    storeSceneDesign,
    storeSceneDesigns,
    storeTemplate,
    storeTemplateCategories,
    storeTemplates,
} from 'js/src/data/templates/templates-actions';
import type {
    DisplayGroupId,
    StoredDisplayGroup,
    StoredSceneDesignIdsPerTemplate,
    StoredTemplate,
    StoredTemplateCategory,
} from 'js/src/data/templates/templates-types';
import type { TemplateCategoryId } from 'js/src/libs/consts';

export function templateCategories(
    state = PlainJsStoreORM.createEmpty<StoredTemplateCategory, TemplateCategoryId>().data,
    action: AnyAction,
) {
    if (storeTemplateCategories.match(action)) {
        const orm = new PlainJsStoreORM<StoredTemplateCategory, TemplateCategoryId>(state);
        action.payload.categories.forEach((category) => {
            orm.pushItem(category, 'id');
        });
        return orm.data;
    }
    return state;
}

type TemplatesListOrm = PlainJsStoreORM<StoredTemplate, string>;

const sortTemplatesByOrder = (templates: readonly Template[]) =>
    [...templates].sort((left, right) => left.order - right.order);

const toStoredTemplate = (template: Template): StoredTemplate =>
    cloneDeep({
        ...template,
        template_data: Migrator.updateTemplateData(template.template_data as OpaqueVideoData),
    });

const isSameValue = (current: unknown, next: unknown) => current === next;

function setTemplateField<Field extends keyof StoredTemplate>(
    state: TemplatesListOrm['data'],
    templateId: string,
    field: Field,
    value: StoredTemplate[Field],
    isSame: (current: StoredTemplate[Field], next: StoredTemplate[Field]) => boolean = isSameValue,
) {
    const orm: TemplatesListOrm = new PlainJsStoreORM(state);
    if (!orm.contains(templateId)) {
        return state;
    }
    orm.updateById(templateId, (template) =>
        isSame(template[field], value) ? template : { ...template, [field]: value },
    );
    return orm.data;
}

export function templatesList(
    state = PlainJsStoreORM.createEmpty<StoredTemplate, string>().data,
    action: AnyAction,
) {
    if (storeTemplates.match(action)) {
        const orm: TemplatesListOrm = new PlainJsStoreORM(state);
        sortTemplatesByOrder(action.payload.templates).forEach((template) => {
            orm.pushItem(toStoredTemplate(template), 'id');
        });
        return orm.data;
    }
    if (storeTemplate.match(action)) {
        const orm: TemplatesListOrm = new PlainJsStoreORM(state);
        orm.pushItem(toStoredTemplate(action.payload.template), 'id');
        return orm.data;
    }
    if (_setTemplateData.match(action)) {
        const orm: TemplatesListOrm = new PlainJsStoreORM(state);
        if (!orm.contains(action.payload.templateId)) {
            return state;
        }
        orm.updateById(action.payload.templateId, (template) => ({
            ...template,
            template_data: cloneDeep(action.payload.data),
        }));
        return orm.data;
    }
    if (setTemplateStatus.match(action)) {
        return setTemplateField(state, action.payload.templateId, 'status', action.payload.status);
    }
    if (setTemplateColorPalettes.match(action)) {
        return setTemplateField(
            state,
            action.payload.templateId,
            'palette_data',
            cloneDeep(action.payload.palettes),
            isEqual,
        );
    }
    if (setTemplateChartColorOverrides.match(action)) {
        return setTemplateField(
            state,
            action.payload.templateId,
            'chart_color_overrides',
            cloneDeep(action.payload.overrides),
            isEqual,
        );
    }
    if (setTemplateDisplayName.match(action)) {
        return setTemplateField(
            state,
            action.payload.templateId,
            'display_name',
            action.payload.displayName,
        );
    }
    if (setTemplateDefaultStoryboardSceneDesign.match(action)) {
        return setTemplateField(
            state,
            action.payload.templateId,
            'default_storyboard_scene_design',
            action.payload.sceneDesignId,
        );
    }
    if (setTemplateDefaultOutroSceneDesign.match(action)) {
        return setTemplateField(
            state,
            action.payload.templateId,
            'default_outro_scene_design',
            action.payload.sceneDesignId,
        );
    }
    if (setTemplateDefaultIconSceneDesign.match(action)) {
        return setTemplateField(
            state,
            action.payload.templateId,
            'default_icon_scene_design',
            action.payload.sceneDesignId,
        );
    }
    if (setTemplateDefaultSceneDesign.match(action)) {
        return setTemplateField(
            state,
            action.payload.templateId,
            'default_scene_design',
            action.payload.sceneDesignId,
        );
    }
    if (setTemplateDefaultTalkingHeadSceneDesign.match(action)) {
        return setTemplateField(
            state,
            action.payload.templateId,
            'default_talking_head_scene_design',
            action.payload.sceneDesignId,
        );
    }
    if (setTemplateDefaultCutawaySceneDesign.match(action)) {
        return setTemplateField(
            state,
            action.payload.templateId,
            'default_cutaway_scene_design',
            action.payload.sceneDesignId,
        );
    }
    if (setTemplateDefaultCaptionSceneDesign.match(action)) {
        return setTemplateField(
            state,
            action.payload.templateId,
            'default_caption_scene_design',
            action.payload.sceneDesignId,
        );
    }
    if (setTemplateDefaultVoiceoverSceneDesign.match(action)) {
        return setTemplateField(
            state,
            action.payload.templateId,
            'default_voiceover_scene_design',
            action.payload.sceneDesignId,
        );
    }
    if (setTemplateImageUrl.match(action)) {
        return setTemplateField(
            state,
            action.payload.templateId,
            'image_url',
            action.payload.imageUrl,
        );
    }
    if (setTemplateMp4Url.match(action)) {
        return setTemplateField(state, action.payload.templateId, 'mp4_url', action.payload.mp4Url);
    }
    if (setTemplateAspectRatioMp4Url.match(action)) {
        return setTemplateField(
            state,
            action.payload.templateId,
            `${action.payload.aspectRatio}_mp4_url`,
            action.payload.mp4Url,
        );
    }
    if (setTemplateTextMinSize.match(action)) {
        return setTemplateField(
            state,
            action.payload.templateId,
            'text_min_size',
            action.payload.textMinSize,
        );
    }
    if (setTemplateTextMaxSize.match(action)) {
        return setTemplateField(
            state,
            action.payload.templateId,
            'text_max_size',
            action.payload.textMaxSize,
        );
    }
    if (setTemplateOrders.match(action)) {
        const orm: TemplatesListOrm = new PlainJsStoreORM(state);
        action.payload.orderedTemplateIds.forEach((templateId, newOrder) => {
            if (orm.contains(templateId)) {
                orm.updateById(templateId, (template) =>
                    template.order === newOrder ? template : { ...template, order: newOrder },
                );
            }
        });
        const reorderedOrm = PlainJsStoreORM.createEmpty<StoredTemplate, string>();
        action.payload.orderedTemplateIds.forEach((templateId) => {
            reorderedOrm.pushItem(orm.getByIdOrThrow(templateId), 'id');
        });
        return reorderedOrm.data;
    }
    if (setTemplateCategoryId.match(action)) {
        return setTemplateField(
            state,
            action.payload.templateId,
            'template_category',
            action.payload.categoryId,
        );
    }
    if (setTemplateDefaultTextSizeOptions.match(action)) {
        return setTemplateField(
            state,
            action.payload.templateId,
            'default_text_size_options',
            [...action.payload.options],
            isEqual,
        );
    }
    if (setTemplateColorShuffleEnabled.match(action)) {
        return setTemplateField(
            state,
            action.payload.templateId,
            'color_shuffle_enabled',
            action.payload.color_shuffle_enabled,
        );
    }
    if (setTemplateEnabledGlobalTemplates.match(action)) {
        return setTemplateField(
            state,
            action.payload.templateId,
            'enabled_global_templates',
            [...action.payload.enabled_global_templates],
            isEqual,
        );
    }
    if (setTemplateIsGlobalTemplate.match(action)) {
        return setTemplateField(
            state,
            action.payload.templateId,
            'is_global_template',
            action.payload.is_global_template,
        );
    }
    if (setTemplateDefaultAspectRatio.match(action)) {
        return setTemplateField(
            state,
            action.payload.templateId,
            'default_aspect_ratio',
            action.payload.default_aspect_ratio,
        );
    }
    return state;
}

const EMPTY_SCENE_DESIGN_IDS: readonly string[] = [];

type SceneDesignIdsOrm = PlainJsStoreORM<StoredSceneDesignIdsPerTemplate, string>;

function ensureTemplateEntry(orm: SceneDesignIdsOrm, templateId: string) {
    if (!orm.contains(templateId)) {
        orm.pushItem(
            { template_id: templateId, scene_design_ids: EMPTY_SCENE_DESIGN_IDS },
            'template_id',
        );
    }
}

function setTemplateSceneDesignIds(
    orm: SceneDesignIdsOrm,
    templateId: string,
    sceneDesignIds: readonly string[],
) {
    orm.updateById(templateId, (entry) =>
        isEqual(entry.scene_design_ids, sceneDesignIds)
            ? entry
            : { ...entry, scene_design_ids: sceneDesignIds },
    );
}

function appendTemplateSceneDesignId(
    orm: SceneDesignIdsOrm,
    templateId: string,
    sceneDesignId: string,
) {
    orm.updateById(templateId, (entry) =>
        entry.scene_design_ids.includes(sceneDesignId)
            ? entry
            : { ...entry, scene_design_ids: [...entry.scene_design_ids, sceneDesignId] },
    );
}

const sortSceneDesignsByOrder = (sceneDesigns: readonly SceneDesign[]) =>
    [...sceneDesigns].sort((sdl, sdr) => sdl.order - sdr.order);

export function sceneDesignIdsByTemplate(
    state = PlainJsStoreORM.createEmpty<StoredSceneDesignIdsPerTemplate, string>().data,
    action: AnyAction,
) {
    if (storeTemplates.match(action)) {
        const orm = new PlainJsStoreORM<StoredSceneDesignIdsPerTemplate, string>(state);
        sortTemplatesByOrder(action.payload.templates).forEach((template) => {
            ensureTemplateEntry(orm, template.id);
        });
        return orm.data;
    }
    if (storeTemplate.match(action)) {
        const orm = new PlainJsStoreORM<StoredSceneDesignIdsPerTemplate, string>(state);
        ensureTemplateEntry(orm, action.payload.template.id);
        return orm.data;
    }
    if (storeSceneDesigns.match(action)) {
        const orm = new PlainJsStoreORM<StoredSceneDesignIdsPerTemplate, string>(state);
        const templateId = action.payload.templateId;
        ensureTemplateEntry(orm, templateId);
        sortSceneDesignsByOrder(action.payload.sceneDesigns).forEach((sceneDesign) => {
            appendTemplateSceneDesignId(orm, templateId, sceneDesign.id);
        });
        return orm.data;
    }
    if (storeSceneDesign.match(action)) {
        const sceneDesign = action.payload.sceneDesign;
        if (typeof sceneDesign.template !== 'string') {
            return state;
        }
        const orm = new PlainJsStoreORM<StoredSceneDesignIdsPerTemplate, string>(state);
        ensureTemplateEntry(orm, sceneDesign.template);
        appendTemplateSceneDesignId(orm, sceneDesign.template, sceneDesign.id);
        return orm.data;
    }
    if (setSceneDesignOrders.match(action)) {
        const { templateId, orderedSceneDesignIds } = action.payload;
        const orm = new PlainJsStoreORM<StoredSceneDesignIdsPerTemplate, string>(state);
        if (!orm.contains(templateId)) {
            return state;
        }
        setTemplateSceneDesignIds(orm, templateId, [...orderedSceneDesignIds]);
        return orm.data;
    }
    if (storeAllSceneDesignIdsPerTemplate.match(action)) {
        const orm = new PlainJsStoreORM<StoredSceneDesignIdsPerTemplate, string>(state);
        action.payload.allSceneDesignIdsPerTemplate.forEach(({ template_id, scene_design_ids }) => {
            if (orm.contains(template_id)) {
                setTemplateSceneDesignIds(orm, template_id, [...scene_design_ids]);
            }
        });
        return orm.data;
    }
    if (clearSceneDesignsForTemplates.match(action)) {
        const orm = new PlainJsStoreORM<StoredSceneDesignIdsPerTemplate, string>(state);
        action.payload.templateIds.forEach((templateId) => {
            if (orm.contains(templateId)) {
                orm.updateById(templateId, (entry) =>
                    entry.scene_design_ids.length === 0
                        ? entry
                        : { ...entry, scene_design_ids: EMPTY_SCENE_DESIGN_IDS },
                );
            }
        });
        return orm.data;
    }
    return state;
}

// TODO: LU-3743 -- this should probably be implemented in Migrator instead
function migrateSceneDesign(sceneDesign: SceneDesign): SceneDesign {
    return VideoData.ALL_ASPECT_RATIOS.reduce(
        (acc, aspectRatio) => {
            const dataKey = `${aspectRatio}_data` as keyof SceneDesign;
            const sceneDesignData = sceneDesign[dataKey];

            if (!sceneDesignData) {
                return acc;
            }

            Object.assign(acc, {
                [dataKey]: Migrator.updateSceneDesignData(sceneDesignData as OpaqueVideoData),
            });
            return acc;
        },
        { ...sceneDesign },
    );
}

export function sceneDesigns(
    state = PlainJsStoreORM.createEmpty<SceneDesign, string>().data,
    action: AnyAction,
) {
    if (storeSceneDesigns.match(action)) {
        const orm = new PlainJsStoreORM<SceneDesign, string>(state);
        sortSceneDesignsByOrder(action.payload.sceneDesigns).forEach((sceneDesign) => {
            orm.pushItem(migrateSceneDesign(sceneDesign), 'id');
        });
        return orm.data;
    }
    if (storeSceneDesign.match(action)) {
        const orm = new PlainJsStoreORM<SceneDesign, string>(state);
        orm.pushItem(migrateSceneDesign(action.payload.sceneDesign), 'id');
        return orm.data;
    }
    if (setSceneDesignAttributesRecommendation.match(action)) {
        const orm = new PlainJsStoreORM<SceneDesign, string>(state);
        orm.updateById(action.payload.sceneDesignId, (item) => ({
            ...item,
            scene_attributes_recommendation: action.payload.attributesRecommendation,
        }));
        return orm.data;
    }
    if (setNonRecommendableAssetNames.match(action)) {
        const orm = new PlainJsStoreORM<SceneDesign, string>(state);
        orm.updateById(action.payload.sceneDesignId, (item) => ({
            ...item,
            non_recommendable_assets: action.payload.nonRecommendableAssetNames,
        }));
        return orm.data;
    }
    if (setSceneDesignData.match(action)) {
        const orm = new PlainJsStoreORM<SceneDesign, string>(state);
        orm.updateById(action.payload.sceneDesignId, (item) => ({
            ...item,
            ...action.payload.data,
        }));
        return orm.data;
    }
    if (setSceneDesignStatus.match(action)) {
        const orm = new PlainJsStoreORM<SceneDesign, string>(state);
        orm.updateById(action.payload.sceneDesignId, (item) => ({
            ...item,
            status: action.payload.status,
        }));
        return orm.data;
    }
    if (setSceneDesignOrders.match(action)) {
        const orm = new PlainJsStoreORM<SceneDesign, string>(state);
        action.payload.orderedSceneDesignIds.forEach((sceneDesignId, newOrder) => {
            orm.updateById(sceneDesignId, (item) => ({
                ...item,
                order: newOrder,
            }));
        });
        return orm.data;
    }
    if (setSceneDesignDisplayGroup.match(action)) {
        const orm = new PlainJsStoreORM<SceneDesign, string>(state);
        orm.updateById(action.payload.sceneDesignId, (item) => ({
            ...item,
            display_group: action.payload.displayGroupId,
        }));
        return orm.data;
    }
    if (setSceneDesignDisplayName.match(action)) {
        const orm = new PlainJsStoreORM<SceneDesign, string>(state);
        orm.updateById(action.payload.sceneDesignId, (item) => ({
            ...item,
            display_name: action.payload.displayName,
        }));
        return orm.data;
    }
    if (clearSceneDesignsForTemplates.match(action)) {
        const templateIdsToRemove = new Set(action.payload.templateIds);
        const sceneDesignIdsToKeep = state.allIds.filter(
            (id) => !templateIdsToRemove.has(VideoData.getTemplateIdFromSceneDesignId(id)),
        );
        const byIdToKeep: Record<string, SceneDesign> = {};
        for (const id of sceneDesignIdsToKeep) {
            byIdToKeep[id] = state.byId[id];
        }
        return {
            byId: byIdToKeep,
            allIds: sceneDesignIdsToKeep,
        };
    }
    return state;
}

export function displayGroups(
    state = PlainJsStoreORM.createEmpty<StoredDisplayGroup, DisplayGroupId>().data,
    action: AnyAction,
) {
    if (storeDisplayGroups.match(action)) {
        const orm = new PlainJsStoreORM<StoredDisplayGroup, DisplayGroupId>(state);
        const { templateId, displayGroups } = action.payload;
        const replacedTemplateIds = new Set([
            templateId,
            ...displayGroups.map((displayGroup) => displayGroup.template),
        ]);
        orm.delete(
            orm
                .filter((displayGroup) => replacedTemplateIds.has(displayGroup.template))
                .map((displayGroup) => displayGroup.id),
        );
        displayGroups.forEach((displayGroup) => {
            orm.pushItem(displayGroup, 'id');
        });
        return orm.data;
    }
    if (setDisplayGroupName.match(action)) {
        const { displayGroupId, displayGroupName } = action.payload;
        const orm = new PlainJsStoreORM<StoredDisplayGroup, DisplayGroupId>(state);
        if (!orm.contains(displayGroupId)) {
            return state;
        }
        orm.updateById(displayGroupId, (displayGroup) =>
            displayGroup.display_name === displayGroupName
                ? displayGroup
                : { ...displayGroup, display_name: displayGroupName },
        );
        return orm.data;
    }
    return state;
}

export function recentSelectedSceneDesigns(
    state: readonly string[] = [],
    action: AnyAction,
): readonly string[] {
    if (storeRecentSelectedSceneDesign.match(action)) {
        const { sceneDesignId } = action.payload;
        return [sceneDesignId, ...state.filter((id) => id !== sceneDesignId)];
    }
    return state;
}

export function activeTemplateCategoryId(
    state: TemplateCategoryId | null = null,
    action: AnyAction,
): TemplateCategoryId | null {
    if (setActiveTemplateCategoryId.match(action)) {
        return action.payload.categoryId;
    }
    return state;
}

export function templateSearchQuery(state: string = '', action: AnyAction): string {
    if (setTemplateSearchQuery.match(action)) {
        return action.payload.query;
    }
    return state;
}

export const templates = combineReducers({
    templateCategories,
    templatesList,
    sceneDesignIdsByTemplate,
    sceneDesigns,
    displayGroups,
    recentSelectedSceneDesigns,
    activeTemplateCategoryId,
    templateSearchQuery,
    loadingInfo: loadingInfoSlice.reducer,
    sceneDesignSelection: sceneDesignSelectionSlice.reducer,
    sceneSequenceDesigns: sceneSequenceDesignsSlice.reducer,
});

export type TemplatesState = ReturnType<typeof templates>;
